import React, { ReactNode } from 'react';
import {
    List,
    Datagrid,
    CreateButton,
    ExportButton,
    TopToolbar,
} from 'react-admin';
import { ListProps } from '../types';
import ColumnSelector from './ColumnSelector';
import DraggableDatagrid from './DraggableDatagrid';
import { ColumnConfig, useColumnReorder } from '../hooks/useColumnReorder';
import BoxitPagination from './BoxitPagination';

export interface CustomizableGridProps extends Omit<ListProps, 'children'> {
    /**
     * Title for the list view
     */
    title?: string;

    /**
     * Default columns configuration
     */
    defaultColumns: ColumnConfig[];

    /**
     * Storage key for persisting column preferences
     */
    storageKey: string;

    /**
     * Function to render a column component based on column ID
     */
    renderColumn: (columnId: string) => ReactNode;

    /**
     * Optional custom pagination component
     */
    paginationComponent?: ReactNode;

    /**
     * Optional custom filter component
     */
    filtersComponent?: ReactNode;

    /**
     * Optional aside component (sidebar)
     */
    asideComponent?: ReactNode;

    /**
     * Sort configuration for the list
     */
    sort?: { field: string; order: string };

    /**
     * Number of items per page
     */
    perPage?: number;

    /**
     * Default filter values
     */
    filterDefaultValues?: any;

    /**
     * Additional meta parameters to pass to the data provider
     */
    metaParams?: any;

    /**
     * Filter object to apply to the list
     */
    filter?: any;

    /**
     * User permissions array
     */
    permissions: string[];

    /**
     * Optional custom bulk action buttons
     */
    bulkActionButtons?: ReactNode;

    /**
     * Optional row click handler
     */
    rowClick?: string | ((id: any, basePath: string, record: any) => void);

    /**
     * Optional custom actions
     */
    actions?: ReactNode;

    /**
     * Optional custom column selector position style
     */
    columnSelectorStyle?: React.CSSProperties;

    /**
     * Optional row style function for custom row styling
     */
    rowStyle?: (record: any, index: number) => React.CSSProperties;

    /**
     * Optional component rendered inside List context, useful for list-side effects
     */
    listContextComponent?: ReactNode;

    /**
     * Extra props passed directly to Datagrid / DraggableDatagrid
     */
    datagridProps?: Record<string, any>;
}

/**
 * A reusable customizable grid component that can be used across all list views
 * Combines column selection, reordering, and persistence functionality
 */
const CustomizableGrid: React.FC<CustomizableGridProps> = ({
    defaultColumns,
    storageKey,
    renderColumn,
    paginationComponent = <BoxitPagination />,
    filtersComponent,
    asideComponent,
    bulkActionButtons,
    rowClick,
    actions,
    columnSelectorStyle,
    rowStyle,
    listContextComponent,
    datagridProps,
    title,
    permissions = [],
    ...rest
}) => {
    // Use the column reorder hook to manage column state
    const {
        columns,
        isColumnVisible,
        handleColumnToggle,
        handleSelectAll,
        handleDeselectAll,
        handleColumnReorder,
    } = useColumnReorder(defaultColumns, storageKey);

    // Detect when bulk actions are present to conditionally render different Datagrid components
    const hasBulkActions =
        bulkActionButtons !== undefined && bulkActionButtons !== null;

    const visibleColumns =
        columns && Array.isArray(columns)
            ? columns.filter(col => col && col.visible).map(col => renderColumn(col.id))
            : [];

    return (
        <>
            <List
                title={title}
                pagination={paginationComponent}
                filters={filtersComponent}
                aside={asideComponent}
                actions={actions}
                bulkActionButtons={bulkActionButtons}
                {...rest}
            >
                <>
                    {listContextComponent}
                    {hasBulkActions ? (
                        <Datagrid
                            rowClick={rowClick}
                            rowStyle={rowStyle}
                            {...datagridProps}
                        >
                            {visibleColumns}
                        </Datagrid>
                    ) : (
                        <DraggableDatagrid
                            columns={columns || []}
                            onColumnReorder={handleColumnReorder}
                            rowClick={rowClick}
                            rowStyle={rowStyle}
                            {...datagridProps}
                        >
                            {visibleColumns}
                        </DraggableDatagrid>
                    )}
                </>
            </List>
            {/* Render ColumnSelector as a floating element */}
            {columns && columns.length > 0 && (
                <ColumnSelector
                    key={`column-selector-${storageKey}`}
                    columns={columns}
                    onColumnToggle={handleColumnToggle}
                    onSelectAll={handleSelectAll}
                    onDeselectAll={handleDeselectAll}
                    onColumnReorder={handleColumnReorder}
                    buttonStyle={columnSelectorStyle}
                />
            )}
        </>
    );
};

export default CustomizableGrid;
